<template>
  <div class="admin-auth-owner-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('adminAuthOwner.page.title')" :subtitle="$t('adminAuthOwner.page.subtitle')" />

    <!-- 小区选择 -->
    <div class="community-switch">
      <select-admin-community @changeCommunity="handleCommunityChange" />
    </div>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.state" :placeholder="$t('adminAuthOwner.search.state')" clearable class="w-full">
            <el-option v-for="item in statusOptions" :key="item.statusCd" :label="item.name"
              :value="item.statusCd" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.appUserName" :placeholder="$t('adminAuthOwner.search.appUserName')"
            clearable @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.idCard" :placeholder="$t('adminAuthOwner.search.idCard')" clearable
            @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.link" :placeholder="$t('adminAuthOwner.search.link')" clearable
            @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('adminAuthOwner.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('adminAuthOwner.page.empty')" @retry="getList">
        <el-table-column prop="communityName" :label="$t('adminAuthOwner.table.communityName')" align="center" min-width="130"
          show-overflow-tooltip />
        <el-table-column :label="$t('adminAuthOwner.table.appUserName')" align="center" min-width="130">
          <template slot-scope="scope">
            <div>{{ scope.row.appUserName }}</div>
            <div v-if="scope.row.ownerId" class="ds-cell-sub">{{ scope.row.ownerId }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="link" :label="$t('adminAuthOwner.table.link')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.link || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="roomName" :label="$t('adminAuthOwner.table.roomName')" align="center" min-width="120">
          <template slot-scope="scope">
            {{ scope.row.roomName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="ownerTypeCdName" :label="$t('adminAuthOwner.table.ownerTypeCdName')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.ownerTypeCdName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="idCard" :label="$t('adminAuthOwner.table.idCard')" align="center" min-width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.idCard || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('adminAuthOwner.table.stateName')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName" :text="scope.row.stateName" type="info" />
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('adminAuthOwner.table.remark')" align="center" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('adminAuthOwner.table.createTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appTypeName" :label="$t('adminAuthOwner.table.appTypeName')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.appTypeName || '-' }}
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>
  </div>
</template>

<script>
import { listAdminAppUserOwners } from '@/api/owner/adminAuthOwnerApi'
import SelectAdminCommunity from '@/components/community/selectAdminCommunity'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AdminAuthOwnerList',
  components: {
    SelectAdminCommunity,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        appUserName: '',
        idCard: '',
        link: '',
        state: '',
        communityId: ''
      },
      statusOptions: [
        { name: this.$t('adminAuthOwner.status.all'), statusCd: '' },
        { name: this.$t('adminAuthOwner.status.pending'), statusCd: '10000' },
        { name: this.$t('adminAuthOwner.status.success'), statusCd: '12000' },
        { name: this.$t('adminAuthOwner.status.failed'), statusCd: '13000' }
      ],
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const { data, total } = await listAdminAppUserOwners(params)
        this.tableData = data
        this.pagination.total = total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('adminAuthOwner.fetchError')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        appUserName: '',
        idCard: '',
        link: '',
        state: '',
        communityId: this.searchForm.communityId
      }
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleCommunityChange(community) {
      this.searchForm.communityId = community.communityId
      this.handleSearch()
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-auth-owner-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 小区切换区 */
.community-switch {
  margin-bottom: var(--ds-spacing-md);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

/* 单元格次要说明 */
.ds-cell-sub {
  font-size: var(--ds-font-size-sm);
  color: var(--ds-gray-500);
}
</style>
